import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { createEffectsHost, type EffectsHost, type EffectsHostOptions } from '../effects/engine';
import { cx } from './shared';

const EffectsContext = createContext<EffectsHost | null>(null);

export interface EffectsHostProps extends EffectsHostOptions {
  /** Fill the nearest positioned ancestor instead of the whole viewport. */
  contained?: boolean;
  /** Receives the host once it exists (e.g. to wire IPC in games' overlay). */
  onReady?: (host: EffectsHost) => void;
  children?: ReactNode;
  className?: string;
}

/**
 * Renders the effects layer and provides useEffects() to everything inside it.
 * One per page (or per contained area).
 */
export function EffectsHost({ contained, onReady, children, className, ...options }: EffectsHostProps) {
  const layerRef = useRef<HTMLDivElement>(null);
  const [host, setHost] = useState<EffectsHost | null>(null);

  useEffect(() => {
    const created = createEffectsHost(layerRef.current!, options);
    setHost(created);
    onReady?.(created);
    return () => created.destroy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <EffectsContext.Provider value={host}>
      {children}
      <div ref={layerRef} className={cx('effects-layer', contained && 'effects-layer--contained', className)} aria-hidden="true" />
    </EffectsContext.Provider>
  );
}

/** The nearest EffectsHost: useEffects()?.play({ effectType: EffectType.TS_FIREWORKS }). */
export function useEffects(): EffectsHost | null {
  return useContext(EffectsContext);
}
